<script setup lang="ts">
import {
    ExtendedMonthlyPurchase,
    ExtendedMonthlyTransfer,
    MonthlyPurchase, MonthlyTransfer,
    Trainer,
    Transaction
} from "../../types/PersologOnlineTypes.ts";
import { computed, onMounted, PropType, Ref, ref, watch } from "vue";
import { LanguageManagementAPI, ProfileAdministrationAPI } from "../../api";
import { useI18n } from "vue-i18n";
import { i18n } from "../../main.ts";
import Chart from "primevue/chart";
import TransferTable from "./TransferTable.vue";
import TransactionTable from "./TransactionTable.vue";
import TableSkeleton from "./TableSkeleton.vue";
import ReclaimDialog from "./ReclaimDialog.vue";
import {
    adjustDateTimes,
    formatDate,
    initializeDates,
    isMobileView,
    parseDate,
    toBackendDateTime
} from "../../utils/GeneralUtils.ts";
import { compareDesc, format, parse } from "date-fns";
import {
    computePurchasesTableTotalQuantity,
    getTrainerName,
    getTrainerWithEmail as trainerWithEmail,
    isSubAccountViewMode,
    mergeTrainerLookup,
    missingTrainerIds,
    shouldPrefetchNextSubAccountPage,
} from "../../utils/PurchaseViewUtils.ts";
import { PROFILE_RECLAIM_FLAG, useFeatureFlag } from "../../composables/useFeatureFlag.ts";

const { t } = useI18n();
const profileReclaimEnabled = useFeatureFlag(PROFILE_RECLAIM_FLAG);

const props = defineProps({
    visibleToggle: {
        type: Boolean,
        default: false
    },
    trainer: {
        type: Object as PropType<Trainer>,
        required: true
    }
});

const emit = defineEmits(["update:visible"]);

const visible = ref(props.visibleToggle);
const transactions = ref<Transaction[]>([]);
const purchases = ref<ExtendedMonthlyPurchase[]>([]);
const chartPurchases = ref<ExtendedMonthlyPurchase[]>([]);
const transfers = ref<ExtendedMonthlyTransfer[]>([]);
const receivedTransfers = ref<ExtendedMonthlyTransfer[]>([]);
const loading = ref(false);
const dates = ref<Date[]>([]);

const allPurchases = ref<Array<MonthlyPurchase | MonthlyTransfer>>([]);
const rawPurchases = ref<MonthlyPurchase[]>([]);
const rawTransfers = ref<MonthlyTransfer[]>([]);
const rawReceivedTransfers = ref<MonthlyTransfer[]>([]);
const subAccountPurchases = ref<MonthlyPurchase[]>([]);
const subAccountTransfers = ref<MonthlyTransfer[]>([]);
const trainerLookup = ref<Record<string, Trainer>>({});
const texts = ref<Record<string, string>>({});
const subAccountPage = ref(0);
const subAccountLoading = ref(false);
const hasMoreSubAccountPurchases = ref(false);
const loadedSubAccountPurchasesCount = ref(0);
const subAccountFetchToken = ref(0);
const subAccountTotalElements = ref(0);
const subAccountTotalQuantity = ref(0);

const SUB_ACCOUNT_PAGE_SIZE = 50;

// Per-row reclaim (RDT-113): opens the dialog prefilled with the row's sub-account and product.
const showReclaimDialog = ref(false);
const reclaimAccount = ref<Trainer>();
const reclaimPresetProduct = ref("");
const reclaimPresetQuantity = ref(0);

function openReclaim(row: ExtendedMonthlyPurchase) {
    if (!row.trainerId) {
        return;
    }
    const account = trainerLookup.value[row.trainerId];
    if (!account) {
        return;
    }
    reclaimAccount.value = account;
    reclaimPresetProduct.value = row.productNo ?? "";
    reclaimPresetQuantity.value = row.quantity;
    showReclaimDialog.value = true;
}

function onReclaimed() {
    retrieveTransactions(false);
}

const chartColorScheme = [
    "#f94144",
    "#f65a38",
    "#f3722c",
    "#f8961e",
    "#f9844a",
    "#f9c74f",
    "#90be6d",
    "#43aa8b",
    "#4d908e",
    "#4d807f"
].sort(() => Math.random() - 0.5);

dates.value = initializeDates();
dates.value = adjustDateTimes(dates.value);

watch(
    () => visible.value,
    () => {
        emit("update:visible", visible.value);
    }
);

watch(
    () => props.visibleToggle,
    () => {
        visible.value = props.visibleToggle;
    }
);

watch(
    () => dates.value,
    async () => {
        dates.value = adjustDateTimes(dates.value);
        await retrieveTransactions();
    }
);

watch(
    () => props.trainer,
    async () => {
        await retrieveTransactions();
    }
);

function resetSubAccountLazyState() {
    subAccountPurchases.value = [];
    subAccountPage.value = 0;
    loadedSubAccountPurchasesCount.value = 0;
    subAccountTotalElements.value = 0;
    subAccountTotalQuantity.value = 0;
    hasMoreSubAccountPurchases.value = false;
    subAccountLoading.value = false;
}

/**
 * Loads the next page of sub-account purchases for the distributor trainer if applicable. Uses a fetch token to ensure that only the latest fetch operation updates the state,
 * preventing race conditions when multiple fetches are triggered in quick succession (e.g., due to rapid date changes or view mode toggling).
 * @param fetchToken The token representing the current fetch operation to prevent race conditions.
 */
async function loadNextSubAccountPurchases(fetchToken: number = subAccountFetchToken.value) {
    const isDistributorAccount = props.trainer?.isDistributorAccount === true;
    const hasMoreSubAccountPurchasesFlag = hasMoreSubAccountPurchases.value === true;
    if (!isDistributorAccount || subAccountLoading.value || !hasMoreSubAccountPurchasesFlag) {
        return;
    }

    subAccountLoading.value = true;

    try {
        const formattedStartDate = toBackendDateTime(dates.value[0]);
        const formattedEndDate = toBackendDateTime(dates.value[1]);
        const pageResponse = await ProfileAdministrationAPI.getPagedSubAccountPurchases({
            trainerId: props.trainer.id,
            from: formattedStartDate,
            to: formattedEndDate,
            page: subAccountPage.value,
            size: SUB_ACCOUNT_PAGE_SIZE,
        });

        if (fetchToken !== subAccountFetchToken.value) {
            return;
        }

        subAccountPurchases.value.push(...pageResponse.purchases);

        await updateTrainerLookupForIds(pageResponse.purchases.map((purchase) => purchase.trainer));

        subAccountPage.value = pageResponse.page + 1;
        subAccountTotalElements.value = pageResponse.totalElements;
        subAccountTotalQuantity.value = pageResponse.totalQuantity;
        loadedSubAccountPurchasesCount.value = subAccountPurchases.value.length;
        hasMoreSubAccountPurchases.value = pageResponse.hasNext;

        if (isSubAccountViewMode(purchasesViewMode.value?.value)) {
            displayTransactions();
        }
    } catch (error) {
        console.error("Failed to load paged sub-account purchases:", error);
        hasMoreSubAccountPurchases.value = false;
    } finally {
        if (fetchToken === subAccountFetchToken.value) {
            subAccountLoading.value = false;
        }
    }
}

async function updateTrainerLookupForIds(ids: string[]) {
    const missingIds = missingTrainerIds(trainerLookup.value, ids);

    if (missingIds.length === 0) {
        return;
    }

    const trainerDetailsResults = await Promise.allSettled(
        missingIds.map((id) => ProfileAdministrationAPI.fetchTrainerDetails(id))
    );

    const resolvedTrainers = trainerDetailsResults
        .filter((result): result is PromiseFulfilledResult<Trainer> => result.status === "fulfilled")
        .map((result) => result.value);

    trainerLookup.value = mergeTrainerLookup(trainerLookup.value, resolvedTrainers);
}


function getTrainer(trainerLookup: { [p: string]: Awaited<Trainer> }, trainer: string) {
    return getTrainerName(trainerLookup, trainer);
}

/**
 * Display name with the email in parentheses, e.g. "May Muster (may.muster@test.com)".
 * Falls back to the plain name when no email is known or the name already is the email.
 */
function getTrainerWithEmail(trainerLookup: { [p: string]: Awaited<Trainer> }, trainer: string) {
    return trainerWithEmail(trainerLookup, trainer);
}

/**
 * Retrieves transactions, purchases, transfers, and received transfers for the authenticated trainer
 * within the selected date range. If the authenticated trainer is a distributor, also retrieves sub-account purchases and transfers for the first batch of sub-accounts.
 * @param withLoading If true, shows a loading indicator while fetching data.
 */
async function retrieveTransactions(withLoading: boolean = true) {
    const currentFetchToken = ++subAccountFetchToken.value;
    if (withLoading) {
        loading.value = true;
    }
    const startFormatted = toBackendDateTime(dates.value[0]);
    const endFormatted = toBackendDateTime(dates.value[1]);

    texts.value = Object.fromEntries(
        (await LanguageManagementAPI.fetchTexts("products")).map((id) => [
            id.identifier,
            id.texts.find((t) => t.language === i18n.global.locale.value)?.text ??
            id.texts.find((t) => t.language === "en")?.text ??
            id.identifier
        ])
    );

    // Fetch data from transactions table
    const rawTransactions = await ProfileAdministrationAPI.getTransactions(
        props.trainer?.id,
        startFormatted,
        endFormatted
    );
    // Transactions arrive already sorted by date (newest first) from the backend.
    transactions.value = rawTransactions.map((t) => ({
        ...t,
        productDescription: texts.value[t.product.productNo]?.concat(` (${t.product.productNo.toUpperCase()})`)
    }));

    rawPurchases.value = await ProfileAdministrationAPI.getPurchases(props.trainer?.id, startFormatted, endFormatted);
    rawTransfers.value = await ProfileAdministrationAPI.getTransfers(props.trainer?.id, startFormatted, endFormatted);
    rawReceivedTransfers.value = await ProfileAdministrationAPI.getReceivedTransfers(
        props.trainer?.id,
        startFormatted,
        endFormatted
    );

    // If distributor, fetch sub-account purchases and transfers for the first page
    if (props.trainer?.isDistributorAccount) {
        resetSubAccountLazyState();
        hasMoreSubAccountPurchases.value = true;
        await loadNextSubAccountPurchases(currentFetchToken);
        subAccountTransfers.value = await ProfileAdministrationAPI.getTransfers(
            props.trainer?.id,
            startFormatted,
            endFormatted
        );
    } else {
        subAccountTransfers.value = [];
        resetSubAccountLazyState();
    }

    const uniqueTrainerIds = Array.from(
        new Set(
            [
                rawPurchases.value.map((p) => p.trainer),
                rawTransfers.value.map((t) => t.trainer),
                rawTransfers.value.map((t) => t.recipient),
                rawReceivedTransfers.value.map((t) => t.trainer),
                rawReceivedTransfers.value.map((t) => t.recipient),
                subAccountPurchases.value.map((s) => s.trainer),
                subAccountTransfers.value.map((s) => s.trainer),
                subAccountTransfers.value.map((s) => s.recipient),
            ].flat()
        )
    );

    trainerLookup.value = {};
    await updateTrainerLookupForIds(uniqueTrainerIds);

    displayTransactions();

    loading.value = false;
}

function displayTransactions() {
    allPurchases.value = []

    const ownMode = purchasesViewMode.value?.value;

    allPurchases.value = [
        ...(ownMode === 1 || ownMode === 3 ? rawPurchases.value : []),
        ...(ownMode === 2 || ownMode === 3 ? subAccountPurchases.value : [])
    ];

    chartPurchases.value = rawPurchases.value.map((p) => ({
        productDescription: texts.value[p.product]?.concat(` (${p.product.toUpperCase()})`),
        quantity: p.quantity,
        trainer: getTrainer(trainerLookup.value, p.trainer),
        recipient: getTrainer(trainerLookup.value, p.trainer),
        email: trainerLookup.value[p.trainer]?.email,
        date: formatDate(p.date, t("formats.datetime")),
        id: p.id,
        manual: true
    }));
    purchases.value = allPurchases.value.map((p) => ({
        productDescription: texts.value[p.product]?.concat(` (${p.product.toUpperCase()})`),
        quantity: p.quantity,
        trainer: getTrainerWithEmail(trainerLookup.value, p.trainer),
        recipient: getTrainerWithEmail(trainerLookup.value, p.trainer),
        email: trainerLookup.value[p.trainer]?.email,
        date: p.date,
        id: p.id,
        manual: true,
        // Sub-account rows carry the raw ids so the reclaim action can prefill its dialog;
        // the distributor's own purchases are not reclaimable.
        trainerId: p.trainer !== props.trainer.id ? p.trainer : undefined,
        productNo: p.product
    }));

    if (isSubAccountViewMode(purchasesViewMode.value?.value)) {
        const transfers = subAccountTransfers.value.filter((t) => !t.manual)
            .map((transfer) => ({
                productDescription: texts.value[transfer.product].concat(` (${transfer.product.toUpperCase()})`),
                quantity: transfer.quantity,
                trainer: getTrainerWithEmail(trainerLookup.value, transfer.trainer),
                recipient: getTrainerWithEmail(trainerLookup.value, transfer.recipient),
                email: trainerLookup.value[transfer.recipient]?.email,
                date: transfer.date,
                id: transfer.id,
                manual: transfer.manual,
                trainerId: transfer.recipient !== props.trainer.id ? transfer.recipient : undefined,
                productNo: transfer.product
            }));
        purchases.value.push(...transfers);
    }

    const autoReceivedTransfers = rawReceivedTransfers.value
        .filter((t) => !t.manual)
        .map((transfer) => ({
            productDescription: texts.value[transfer.product]?.concat(` (${transfer.product.toUpperCase()})`),
            quantity: transfer.quantity,
            trainer: getTrainerWithEmail(trainerLookup.value, transfer.trainer),
            recipient: getTrainerWithEmail(trainerLookup.value, transfer.recipient),
            email: trainerLookup.value[transfer.recipient]?.email,
            date: transfer.date,
            id: transfer.id,
            manual: false
        }));

    purchases.value.push(...autoReceivedTransfers);
    purchases.value.sort((a, b) => compareDesc(a.date, b.date));

    transfers.value = rawTransfers.value
        .filter((t) => t.manual)
        .map((transfer) => ({
            productDescription: texts.value[transfer.product].concat(` (${transfer.product.toUpperCase()})`),
            quantity: transfer.quantity,
            trainer: getTrainerWithEmail(trainerLookup.value, transfer.trainer),
            recipient: getTrainerWithEmail(trainerLookup.value, transfer.recipient),
            email: trainerLookup.value[transfer.recipient]?.email,
            date: transfer.date,
            id: transfer.id,
            manual: transfer.manual,
            source: transfer.source
        }));

    transfers.value.sort((a, b) => compareDesc(a.date, b.date));

    receivedTransfers.value = rawReceivedTransfers.value
        .filter((t) => t.manual)
        .map((transfer) => ({
            productDescription: texts.value[transfer.product].concat(` (${transfer.product.toUpperCase()})`),
            quantity: transfer.quantity,
            trainer: getTrainerWithEmail(trainerLookup.value, transfer.trainer),
            recipient: getTrainerWithEmail(trainerLookup.value, transfer.trainer),
            email: trainerLookup.value[transfer.trainer]?.email,
            date: transfer.date,
            id: transfer.id,
            manual: transfer.manual,
            source: transfer.source
        }));

    receivedTransfers.value.sort((a, b) => compareDesc(a.date, b.date));

    chartData.value = setChartData();
}

watch(visible, (value) => {
    if (value) {
        retrieveTransactions(false);
    }
});

onMounted(async () => {
    await retrieveTransactions();
    initializeDates();
    chartOptions.value = setChartOptions();
});

const chartData = ref();
const chartOptions = ref();
const chartPadding = ref(true);
const purchasesViewMode: Ref<{
    name: string,
    value: number
}> = ref({ name: t("header.accountSettings.distributor.own.purchases.own"), value: 1 });

const columns = [
    { field: "product", text: "header.accountSettings.transactionTable.product" },
    { field: "quantity", text: "header.accountSettings.transactionTable.quantity" },
    { field: "trainer", text: "header.accountSettings.transactionTable.trainer" },
    { field: "date", text: "header.accountSettings.transactionTable.date" }
];

const setChartData = () => {
    if (chartPurchases.value.length == 0) return;

    let from: Date;
    let to: Date;

    if (chartPadding.value) {
        from = dates.value[0];
        to = dates.value[1] ?? dates.value[0];
    } else {
        const purchaseDates = chartPurchases.value.map((p) => parseDate(p.date, t("formats.datetime")).getTime());
        from = new Date(Math.min(...purchaseDates));
        to = new Date(Math.max(...purchaseDates));
    }

    // Helper function to get all dates between from and to
    const getAllDates = (startDate: Date, endDate: Date) => {
        const datesRange = [];
        const currentDate = new Date(startDate);
        while (currentDate <= endDate) {
            datesRange.push(format(currentDate, t("formats.date")));
            currentDate.setDate(currentDate.getDate() + 1);
        }
        return datesRange;
    };

    const allDates = getAllDates(from, to);
    const dateIndex: { [key: string]: number | undefined } = allDates.reduce((acc, d, i) => ({ ...acc, [d]: i }), {});

    // Initialize data structures for each product and total quantities
    const productData: { [key: string]: number[] } = {};
    const totalQuantities: number[] = new Array(allDates.length).fill(0);

    const uniqueProducts = Array.from(new Set(chartPurchases.value.map((p) => p.id)));
    uniqueProducts.forEach((productId) => (productData[productId as number] = [...totalQuantities]));

    // Populate data for each product and total quantities
    chartPurchases.value.forEach((purchase) => {
        const parsedDate = parse(purchase.date, t("formats.datetime"), new Date());
        const formattedDate = format(parsedDate, t("formats.date"));
        const index = dateIndex[formattedDate];
        if (index !== undefined) {
            productData[purchase.id as number][index] += purchase.quantity;
            totalQuantities[index] += purchase.quantity;
        }
    });

    interface Dataset {
        type: string;
        label: string;
        borderColor: string;
        backgroundColor: string;
        data: number[];
        borderWidth?: number;
        pointRadius?: number;
        pointHoverRadius?: number;
        fill?: boolean;
        tension?: number;
    }

    // Build datasets for Chart.js
    let colorIndex = 0;
    const datasets = uniqueProducts.map((productId) => {
        colorIndex = (colorIndex + 1) % chartColorScheme.length;
        const productName = chartPurchases.value.find((p) => p.id === productId)?.productDescription || productId;
        return {
            type: "bar",
            label: productName,
            borderColor: "white",
            backgroundColor: chartColorScheme[colorIndex],
            data: productData[productId as number]
        } as Dataset;
    });

    // Add the total quantities dataset
    datasets.push({
        type: "line",
        label: "Total",
        // Theme token from @persolog/theme. An undefined variable reads as "" and Chart.js then draws black.
        borderColor: getComputedStyle(document.documentElement).getPropertyValue("--ui-brand"),
        borderWidth: 1,
        backgroundColor: "transparent",
        pointRadius: 1,
        pointHoverRadius: 4,
        fill: false,
        tension: 0.4,
        data: totalQuantities
    });

    return {
        labels: allDates,
        datasets: datasets
    };
};

const onPaddingChanged = () => (chartData.value = setChartData());

const loadedSubAccountPurchasesQuantity = computed(() =>
    subAccountPurchases.value.reduce((sum, purchase) => sum + purchase.quantity, 0)
);

/**
 * Unfiltered total for the distributor purchases table. Sub-account purchases stream in page by
 * page, so the sum of the displayed rows alone would grow while paging; add the quantity of the
 * sub-account purchases in the selected range that are not loaded yet, so the label already shows
 * the total the table will contain once fully loaded.
 */
const purchasesTableTotalQuantity = computed(() => {
    const displayedQuantity = purchases.value.reduce((sum, purchase) => sum + purchase.quantity, 0);
    return computePurchasesTableTotalQuantity(
        displayedQuantity,
        purchasesViewMode.value.value,
        subAccountTotalQuantity.value,
        loadedSubAccountPurchasesQuantity.value,
    );
});

function updatePurchasesViewMode(value: { name: string, value: number }) {
    purchasesViewMode.value = value;
    displayTransactions();
}

async function handlePurchasesPageChange(event: { first: number; rows: number }) {
    if (!isSubAccountViewMode(purchasesViewMode.value.value)) {
        return;
    }

    const shouldPrefetchNextPage = shouldPrefetchNextSubAccountPage(
        event.first,
        event.rows,
        purchases.value.length,
    );

    if (shouldPrefetchNextPage && hasMoreSubAccountPurchases.value && !subAccountLoading.value) {
        await loadNextSubAccountPurchases();
    }
}

const setChartOptions = () => {
    const documentStyle = getComputedStyle(document.documentElement);
    const textColor = documentStyle.getPropertyValue("--text-color");
    const textColorSecondary = documentStyle.getPropertyValue("--text-color-secondary");
    const surfaceBorder = documentStyle.getPropertyValue("--surface-border");

    return {
        maintainAspectRatio: false,
        plugins: {
            legend: {
                labels: { color: textColor }
            },
            tooltips: {
                mode: "index",
                intersect: false
            }
        },
        scales: {
            x: {
                ticks: { color: textColorSecondary },
                grid: { color: surfaceBorder }
            },
            y: {
                beginAtZero: true,
                ticks: { color: textColorSecondary },
                grid: { color: surfaceBorder }
            }
        }
    };
};
</script>

<template>
    <Dialog
        v-model:visible="visible"
        id="purchase-view_dialog_purchases"
        modal
        :style="{ minWidth: '85vw' }"
        :breakpoints="{ '1199px': '85vw', '575px': '97vw' }"
        :dismissable-mask="false"
        :block-scroll="true"
        :draggable="false"
    >
        <template #header>
            <div class="inline-flex items-center flex-wrap gap-8">
                <span class="font-bold whitespace-nowrap">
                    {{ t("header.accountSettings.transactions") }} - {{ trainer?.firstname }} {{ trainer?.lastname }}
                </span>
                <DatePicker
                    v-model="dates"
                    selection-mode="range"
                    show-icon
                    icon-display="input"
                    :manual-input="true"
                    :touch-u-i="isMobileView()"
                    show-button-bar
                    :number-of-months="isMobileView() ? 1 : 2"
                    :date-format="t('formats.datePicker')"
                    :max-date="new Date(Date.now())"
                />
            </div>
        </template>

        <div v-if="purchases.length > 0" style="position: relative">
            <ToggleSwitch
                v-model="chartPadding"
                @change="onPaddingChanged"
                style="position: absolute; right: 1.5rem; z-index: 1; transform: scale(0.65)"
                v-tooltip.top="{ value: 'Show full range', showDelay: 500 }"
            />
            <Chart type="bar" :data="chartData" :options="chartOptions" class="h-60 w-full" />
        </div>

        <div class="mt-8">
            <TableSkeleton v-if="loading" :columns="columns" />
            <TransactionTable
                v-else
                :with-icon="trainer.isDistributorAccount"
                :transactions="transactions"
                :headline="t('header.accountSettings.transactions')"
                :tooltip-message="t('trainer-transactions.transaction-table.tooltips.transactions')"
                :rows="10"
            />
        </div>

        <div class="mt-8" v-if="trainer.isDistributorAccount">
            <TableSkeleton
                v-if="loading"
                :columns="[
                    { field: 'product', text: 'header.accountSettings.transactionTable.product' },
                    { field: 'quantity', text: 'header.accountSettings.transactionTable.quantity' },
                    { field: 'trainer', text: 'header.accountSettings.transactionTable.balanceActivityType' },
                    { field: 'trainer', text: 'header.accountSettings.transactionTable.trainer' },
                    { field: 'date', text: 'header.accountSettings.transactionTable.date' },
                ]"
            />
            <TransferTable
                    v-else
                    :with-own-purchases="purchasesViewMode"
                    :display-own-option="true"
                    :with-icon="true"
                    :with-reclaim="profileReclaimEnabled"
                    type="purchase"
                    :transactions="purchases"
                    @own-purchases="updatePurchasesViewMode"
                    @page-change="handlePurchasesPageChange"
                    @reclaim="openReclaim"
                    :total-quantity-override="purchasesTableTotalQuantity"
                    :headline="t('header.accountSettings.purchases')"
                    :tooltip-message="t('trainer-transactions.transfer-table.tooltips.purchasesDistributorView')"
                />
        </div>

        <div class="mt-8" v-else>
            <TableSkeleton v-if="loading" :columns="columns" />
            <TransferTable
                v-else
                type="purchase"
                :transactions="purchases"
                :headline="t('header.accountSettings.purchases')"
                :tooltip-message="t('trainer-transactions.transfer-table.tooltips.purchases')"
            />
        </div>

        <div class="mt-8">
            <TableSkeleton v-if="loading" :columns="columns" />
            <TransferTable
                v-else
                type="transfer"
                direction="transferred"
                :with-source="true"
                :transactions="transfers"
                :headline="t('header.accountSettings.transfers.transferred')"
                :tooltip-message="t('trainer-transactions.transfer-table.tooltips.transferred')"
            />
        </div>

        <div class="mt-8">
            <TableSkeleton v-if="loading" :columns="columns" />
            <TransferTable
                v-else
                type="transfer"
                direction="received"
                :transactions="receivedTransfers"
                :headline="t('header.accountSettings.transfers.received')"
                :tooltip-message="t('trainer-transactions.transfer-table.tooltips.received')"
            />
        </div>

        <template #footer>
            <div class="flex justify-end gap-2 mt-8">
                <Button :label="t('buttons.close')" @click="visible = !visible" size="small" autofocus />
            </div>
        </template>
    </Dialog>
    <ReclaimDialog
        v-if="profileReclaimEnabled && reclaimAccount && showReclaimDialog"
        :visible-toggle="showReclaimDialog"
        :account="reclaimAccount"
        :trainer="props.trainer"
        :preset-product="reclaimPresetProduct"
        :preset-quantity="reclaimPresetQuantity"
        @update:visible="(value) => (showReclaimDialog = value)"
        @retrieved="onReclaimed"
    />
</template>
